import { useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';

import {
  ATSConnectHiringRequestCreateReferenceContactIds,
  ATSConnectHiringRequestRequestReferenceTestIds,
} from 'data-testids/ATS';
import { Box } from '@mui/material';
import { ReferenceContactAndTemplateForm } from './components/ReferenceContactAndTemplateForm';
import { ReferenceCreateCTAButtons } from './components/ReferenceCreateCTAButtons';
import { ReferenceCreateLandingPageProps, ReferenceRequestInitialFormState } from './types';
import { ReferenceHeader } from './components/ReferenceHeader';
import { ReferenceToggle } from './components/ReferenceToggle';
import { RequestReferenceCheck } from './Request/RequestReferenceCheck';
import { ToggleButtonAlignment } from 'sharedComponents/Buttons/ToggleButtons';
import { ToggleButtonCurrentlySelected } from 'sharedComponents/Buttons/types';
import { WriteReferenceCheck } from './Write/WriteReferenceCheck';
import { ReferenceRequestPayload, useCreateReferenceRequest } from './mutations';
import { ErrorSnackbar } from 'sharedComponents/Snackbar';
import { getAxiosErrorMessageByStatusCode } from 'hooks/errorHandling/errorHandlingHooks';
import { useCreateHiringRequestReference } from './Write/mutations';

export const ReferenceCreateLandingPage: React.FC<ReferenceCreateLandingPageProps> = ({
  candidateId,
  candidateName,
  hiringRequestId,
  newReferenceContact,
  onAddClick,
  onCancel,
  onSuccess,
}) => {
  const [currentReferenceToggle, setCurrentReferenceToggle] =
    useState<ToggleButtonCurrentlySelected>(ToggleButtonAlignment.LEFT_TOGGLE_ACTIVE);
  const [showErrorToast, setShowErrorToast] = useState(false);

  const createRequestMethods = useForm({
    reValidateMode: 'onChange',
    defaultValues: {
      candidate_id: candidateId,
      subject: '',
      message: '',
      recipients: newReferenceContact,
    },
  });

  const { handleSubmit: handleRequestSubmit } = createRequestMethods;
  const {
    mutate: createReferenceRequest,
    error: createReferenceRequestError,
    isLoading,
  } = useCreateReferenceRequest(candidateId, {
    onSuccess: () => {
      onSuccess('Successfully sent');
    },
    onError: () => {
      setShowErrorToast(true);
    },
  });

  const createReferenceMethods = useForm({
    reValidateMode: 'onChange',
    defaultValues: {
      reference_id: newReferenceContact?.id,
    },
  });
  const { handleSubmit: handleWriteSubmit } = createReferenceMethods;

  const { mutate: createReference, error: createReferenceError } = useCreateHiringRequestReference(
    candidateId,
    {
      onSuccess: () => {
        onSuccess('Successfully created reference');
      },
      onError: () => {
        setShowErrorToast(true);
      },
    }
  );

  const handleToggle = () => {
    if (currentReferenceToggle === ToggleButtonAlignment.LEFT_TOGGLE_ACTIVE) {
      setCurrentReferenceToggle(ToggleButtonAlignment.RIGHT_TOGGLE_ACTIVE);
    } else {
      setCurrentReferenceToggle(ToggleButtonAlignment.LEFT_TOGGLE_ACTIVE);
    }
  };

  const handleCreateRequest = (formState: ReferenceRequestInitialFormState | FieldValues) => {
    const formStateToSubmit: ReferenceRequestPayload = {
      ...(formState as ReferenceRequestInitialFormState),
      recipients: [formState.recipients],
      role_title: '',
      is_reminder: false,
    };

    createReferenceRequest(formStateToSubmit);
  };

  const handleCreateReference = (formState) => {
    const formData = new FormData();

    const processedAnswers = formState.answers.map((answer) => {
      if (answer.attachment instanceof File) {
        formData.append(answer.attachment.name, answer.attachment);
        return {
          ...answer,
          attachment: answer.attachment.name,
        };
      }

      return answer;
    });

    const payload = {
      ...formState,
      answers: processedAnswers,
      scorecard_complete: true,
      candidate_id: candidateId,
      submitted_by_name: formState.recipients.name || newReferenceContact.name,
    };

    formData.append('data', JSON.stringify(payload));

    createReference({ payload: formData });
  };

  const errorMessage =
    getAxiosErrorMessageByStatusCode(createReferenceRequestError) ||
    getAxiosErrorMessageByStatusCode(createReferenceError);

  return (
    <Box data-testid={ATSConnectHiringRequestCreateReferenceContactIds.LANDING_PAGE}>
      <ReferenceHeader candidateName={candidateName} />
      <Box sx={{ py: 2 }}>
        {currentReferenceToggle === ToggleButtonAlignment.LEFT_TOGGLE_ACTIVE && (
          <FormProvider {...createReferenceMethods}>
            <form onSubmit={handleWriteSubmit(handleCreateReference)}>
              <ReferenceContactAndTemplateForm
                candidateId={candidateId}
                isWriteReferenceCheck={true}
                newReferenceContactId={newReferenceContact?.id}
                onAddClick={onAddClick}
              />
              <ReferenceToggle currentlySelected={currentReferenceToggle} onToggle={handleToggle} />
              <WriteReferenceCheck />
              <ReferenceCreateCTAButtons isLoading={isLoading} onBackClick={onCancel} />
            </form>
          </FormProvider>
        )}
        {currentReferenceToggle === ToggleButtonAlignment.RIGHT_TOGGLE_ACTIVE && (
          <FormProvider {...createRequestMethods}>
            <form onSubmit={handleRequestSubmit(handleCreateRequest)}>
              <ReferenceContactAndTemplateForm
                candidateId={candidateId}
                isWriteReferenceCheck={false}
                newReferenceContactId={newReferenceContact?.id}
                onAddClick={onAddClick}
              />
              <ReferenceToggle currentlySelected={currentReferenceToggle} onToggle={handleToggle} />
              <RequestReferenceCheck hiringRequestId={hiringRequestId} candidateId={candidateId} />
              <ReferenceCreateCTAButtons isLoading={isLoading} onBackClick={onCancel} />
            </form>
          </FormProvider>
        )}
      </Box>
      <ErrorSnackbar
        message={errorMessage}
        dataTestId={ATSConnectHiringRequestRequestReferenceTestIds.ERROR_TOAST_REQUEST_REFERENCE}
        open={showErrorToast}
        onClose={() => setShowErrorToast(false)}
      />
    </Box>
  );
};
